/**
 * ConfigGluonPage
 *
 * Admin page to configure the Gluon Link service connection for this org.
 * Reads/writes organisation/{org_id}/config/gluon_link in Firestore via
 * the /admin/config/gluon_link endpoints.
 *
 * Route: /settings/config/gluon  (admin-only)
 */

import { useCallback, useEffect, useState } from "react";
import useFeedback from "../../hooks/useFeedback";
import SettingsPageShell from "../../components/Settings/SettingsPageShell";
import { PrimaryButton, CompactButton } from "../../components/Button/Buttons";
import Input from "../../components/Inputs/Input";
import Select from "../../components/Inputs/Select";
import Loader from "../../components/Loader/Loader";
import ConfirmDialog from "../../components/shared/ConfirmDialog/ConfirmDialog";
import {
  getServiceConfig, updateServiceConfig,
  getServiceCredential, updateServiceCredential, resetServiceCredential,
  getGluonCredential, updateGluonCredential, resetGluonCredential,
  rotateGluonKeypair, sealGluonKeypair,
} from "../../api/adminConfig";
import { AuthError } from "../../api";
import UIStore from "../../store/uiStore";
import Toggle from "../../components/shared/Toggle";
import CredentialField from "../../components/Settings/CredentialField";
import { isDirty } from "../../utils/isDirty";

const GLUON_CRED_FIELDS = [
  { key: "api_key", label: "Service Token", placeholder: "••••••••", type: "password" },
];

const MODE_OPTIONS = [
  { value: "remote", label: "Remote" },
  { value: "local",  label: "Local"  },
];

const AUTH_OPTIONS = [
  { value: "gcp",   label: "GCP Identity Token" },
  { value: "token", label: "Service Token"       },
  { value: "none",  label: "None"                },
];

const EMPTY_FORM = {
  enabled:       true,
  url:           "",
  timeout:       300,
  mode:          "remote",
  auth_type:     "gcp",
  jwt_server_url: "",
  public_key:    "",
};

const ConfigGluonPage = () => {
  const { showFeedback } = useFeedback();
  const orgAvailable = UIStore((s) => s.orgAvailable);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [originalForm, setOriginalForm] = useState(EMPTY_FORM);
  const [keypairHasPlaintext, setKeypairHasPlaintext] = useState(false);
  const [keypairStatus, setKeypairStatus] = useState({ configured: false });
  const [keypairDraft, setKeypairDraft] = useState({ private_key_pem: "", public_key: "" });
  const [keypairAction, setKeypairAction] = useState("");
  const [editingKeypair, setEditingKeypair] = useState(false);
  const [confirmingSave, setConfirmingSave] = useState(false);
  const [sealingKeypair, setSealingKeypair]           = useState(false);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getServiceConfig("gluon_link");
      if (data.configured) {
        const loaded = {
          enabled:        data.enabled        ?? true,
          url:            data.url            ?? "",
          timeout:        data.timeout        ?? 300,
          mode:           data.mode           ?? "remote",
          auth_type:      data.auth_type      ?? "gcp",
          jwt_server_url: data.jwt_server_url ?? "",
          public_key:     data.public_key     ?? "",
        };
        setForm(loaded);
        setOriginalForm(loaded);
      } else {
        setForm(EMPTY_FORM);
        setOriginalForm(EMPTY_FORM);
      }
    } catch (err) {
      if (!(err instanceof AuthError)) showFeedback("Failed to load Gluon Link config.", "error");
    } finally {
      setLoading(false);
    }
  }, [showFeedback]);

  useEffect(() => {
    if (orgAvailable) fetchConfig();
  }, [orgAvailable, fetchConfig]);

  // Stable callback references — prevents CredentialField's fetchStatus useEffect
  // from re-firing on every parent render (inline functions would create new refs each time).
  const fetchServiceTokenStatus  = useCallback(() => getServiceCredential("gluon_link"), []);
  const saveServiceToken         = useCallback((cred) => updateServiceCredential("gluon_link", cred), []);
  const revokeServiceToken       = useCallback(() => resetServiceCredential("gluon_link"), []);

  const fetchKeypairStatus = useCallback(async () => {
    const data = await getGluonCredential("keypair");
    setKeypairHasPlaintext(!data.configured && !!data.has_plaintext);
    setKeypairStatus(data);
    return data;
  }, []);

  useEffect(() => {
    if (orgAvailable) fetchKeypairStatus();
  }, [orgAvailable, fetchKeypairStatus]);

  const handleRotateKeypair = useCallback(async () => {
    try {
      const result = await rotateGluonKeypair();
      setKeypairDraft({
        private_key_pem: result.private_key || "",
        public_key: result.public_key || "",
      });
      setKeypairAction("replace");
      setEditingKeypair(false);
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to generate keypair.", "error");
    }
  }, [showFeedback]);

  const handleSealKeypair = async () => {
    setSealingKeypair(true);
    try {
      await sealGluonKeypair();
      setKeypairHasPlaintext(false);
      showFeedback("Bootstrap keypair sealed successfully.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to seal keypair.", "error");
    } finally {
      setSealingKeypair(false);
    }
  };

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target ? e.target.value : e }));

  const discardDrafts = () => {
    setForm(originalForm);
    setKeypairDraft({ private_key_pem: "", public_key: "" });
    setKeypairAction("");
    setEditingKeypair(false);
  };

  const persistChanges = async () => {
    setSaving(true);
    try {
      if (isDirty(form, originalForm)) {
        await updateServiceConfig("gluon_link", {
          enabled: form.enabled,
          url: form.url,
          timeout: Number(form.timeout) || 300,
          mode: form.mode,
          auth_type: form.auth_type,
          jwt_server_url: form.jwt_server_url,
        });
      }
      let savedForm = form;
      if (keypairAction === "replace") {
        const normalizedKeypair = {
          private_key_pem: keypairDraft.private_key_pem.replace(/\\n/g, "\n").trim(),
          public_key: keypairDraft.public_key.replace(/\\n/g, "\n").trim(),
        };
        await updateGluonCredential("keypair", normalizedKeypair);
        savedForm = { ...form, public_key: normalizedKeypair.public_key };
      } else if (keypairAction === "revoke") {
        await resetGluonCredential("keypair");
        savedForm = { ...form, public_key: "" };
      }
      setForm(savedForm);
      setOriginalForm(savedForm);
      setKeypairDraft({ private_key_pem: "", public_key: "" });
      setKeypairAction("");
      setEditingKeypair(false);
      await fetchKeypairStatus();
      showFeedback("Gluon Link configuration saved.", "success");
    } catch (err) {
      showFeedback(err?.response?.data?.error || "Failed to save Gluon Link config.", "error");
    } finally {
      setSaving(false);
      setConfirmingSave(false);
    }
  };

  const keypairDirty = !!keypairAction;
  const pageDirty = isDirty(form, originalForm) || keypairDirty;
  const handleSave = () => {
    if (keypairDirty) {
      setConfirmingSave(true);
      return;
    }
    persistChanges();
  };

  return (
    <SettingsPageShell
      title="Gluon Link Configuration"
      description="Configure the connection to the Gluon Link service used for agent orchestration."
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-6 max-w-lg">
          {/* URL */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Service URL
            </label>
            <Input
              type="url"
              placeholder={form.mode === "local" ? "Not used in STDIO mode" : "https://gluon.example.com"}
              value={form.mode === "local" ? "" : form.url}
              onChange={set("url")}
              disabled={form.mode === "local"}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              {form.mode === "local"
                ? "STDIO mode — the SDK is spawned as a local subprocess; no remote URL is needed"
                : "Base URL of the Gluon Link Cloud Run instance"}
            </p>
          </div>

          {/* Timeout */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Timeout (seconds)
            </label>
            <Input
              type="number"
              min={1}
              max={300}
              placeholder="300"
              value={form.timeout}
              onChange={set("timeout")}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">Per tool-call request timeout before a single call is aborted (max 300s; remote mode is hard-capped at 300s by Cloud Run). Does not cap total agent run time.</p>
          </div>

          {/* Mode */}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
              Mode
            </label>
            <Select
              value={form.mode}
              onChange={(v) => setForm((prev) => ({ ...prev, mode: v }))}
              options={MODE_OPTIONS}
            />
            <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
              {form.mode === "local"
                ? "SDK runs in-process — JWT is signed locally using the RS256 keypair below"
                : "Requests are forwarded to the Gluon Link Cloud Run server, which handles JWT signing"}
            </p>
          </div>

          {/* Auth type — only relevant in remote mode; SDK calls MCP directly in local mode */}
          {form.mode !== "local" && (
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                Authentication
              </label>
              <Select
                value={form.auth_type}
                onChange={(v) => setForm((prev) => ({ ...prev, auth_type: v }))}
                options={AUTH_OPTIONS}
              />
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                {form.auth_type === "gcp"
                  ? "Attaches a GCP identity token to each request — requires the backend service account to have invoker access on the GL Cloud Run service"
                  : form.auth_type === "token"
                  ? "Sends a static service token in the Authorization header — configure the token below"
                  : "No authentication header is sent — only use with internally-accessible Gluon Link instances"}
              </p>
            </div>
          )}

          {/* JWKS Server URL — shown in all modes */}
          <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">
                JWKS Server URL
              </label>
              <Input
                type="url"
                placeholder="https://jwks.example.com"
                value={form.jwt_server_url}
                onChange={set("jwt_server_url")}
              />
              <p className="text-[11px] text-ink-gray-400 dark:text-ink-gray-500">
                Public base URL of the JWKS endpoint used to validate signed JWTs. Set as the <code>iss</code> claim — MCP nodes fetch <code>{"/.well-known/openid-configuration"}</code> from this URL to verify tokens
              </p>
            </div>

          {/* Service token credential — shown when auth type is token and not local mode */}
          {form.mode !== "local" && form.auth_type === "token" && (
            <CredentialField
              title="Service Token"
              fields={GLUON_CRED_FIELDS}
              onFetch={fetchServiceTokenStatus}
              onSave={saveServiceToken}
              onRevoke={revokeServiceToken}
            />
          )}

          {/* RS256 keypair credential — always visible so switching mode doesn't lose a generated key */}
          {keypairHasPlaintext ? (
            <div className="flex flex-col gap-2 rounded-[10px] border border-edge-amber-400/60 bg-fill-hex-fffbeb dark:bg-fill-hex-78350f/20 p-4">
              <p className="text-xs font-semibold text-ink-amber-700 dark:text-ink-amber-400">
                Bootstrap keypair detected
              </p>
              <p className="text-[11px] text-ink-amber-700/80 dark:text-ink-amber-400/70">
                A plaintext private key was written by the setup script. Seal it into secure storage so Gluon Link can use it.
              </p>
              <div>
                <CompactButton onClick={handleSealKeypair} disabled={sealingKeypair}>
                  {sealingKeypair ? "Sealing…" : "Seal keypair"}
                </CompactButton>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-3 rounded-lg border border-edge-gray-200 dark:border-edge-gray-700 p-4">
              <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">RS256 Keypair</p>
              {keypairAction === "revoke" ? (
                <>
                  <p className="text-xs text-ink-red-600 dark:text-ink-red-400">Pending deletion. Save to revoke the complete keypair.</p>
                  <div><CompactButton onClick={() => setKeypairAction("")}>Undo</CompactButton></div>
                </>
              ) : keypairAction === "replace" ? (
                <>
                  <p className="text-xs text-ink-amber-700 dark:text-ink-amber-400">Replacement staged. Save to activate this keypair.</p>
                  <div><CompactButton onClick={() => { setKeypairAction(""); setKeypairDraft({ private_key_pem: "", public_key: "" }); }}>Undo</CompactButton></div>
                </>
              ) : editingKeypair ? (
                <>
                  <label className="text-xs text-ink-gray-700 dark:text-ink-gray-300">RS256 Private Key (PEM)</label>
                  <textarea
                    aria-label="RS256 Private Key (PEM)"
                    className="photon-field bg-fill-hex-fafbfe dark:bg-fill-navigation-surface-dark block w-full p-[10px] text-[13px] font-mono text-ink-ink dark:text-ink-white placeholder-ink-copy-hover outline-none border-[0.5px] border-edge-hex-aaaaaa dark:border-edge-hex-aaaaaa80 rounded-[10px] placeholder:italic resize-y min-h-[110px] leading-[1.5]"
                    value={keypairDraft.private_key_pem}
                    onChange={(event) => setKeypairDraft((previous) => ({ ...previous, private_key_pem: event.target.value }))}
                  />
                  <label className="text-xs text-ink-gray-700 dark:text-ink-gray-300">RS256 Public Key (PEM)</label>
                  <textarea
                    aria-label="RS256 Public Key (PEM)"
                    className="photon-field bg-fill-hex-fafbfe dark:bg-fill-navigation-surface-dark block w-full p-[10px] text-[13px] font-mono text-ink-ink dark:text-ink-white placeholder-ink-copy-hover outline-none border-[0.5px] border-edge-hex-aaaaaa dark:border-edge-hex-aaaaaa80 rounded-[10px] placeholder:italic resize-y min-h-[110px] leading-[1.5]"
                    value={keypairDraft.public_key}
                    onChange={(event) => setKeypairDraft((previous) => ({ ...previous, public_key: event.target.value }))}
                  />
                  <div className="flex gap-2">
                    <PrimaryButton
                      onClick={() => { setKeypairAction("replace"); setEditingKeypair(false); }}
                      disabled={!keypairDraft.private_key_pem.trim() || !keypairDraft.public_key.trim()}
                    >
                      Stage keypair
                    </PrimaryButton>
                    <CompactButton onClick={() => { setEditingKeypair(false); setKeypairDraft({ private_key_pem: "", public_key: "" }); }}>Cancel</CompactButton>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">
                    {keypairStatus.configured ? `Configured / ••••${keypairStatus.last4 || ""}` : "Not configured."}
                  </p>
                  <div className="flex gap-2">
                    <CompactButton onClick={handleRotateKeypair}>Rotate</CompactButton>
                    <CompactButton onClick={() => setEditingKeypair(true)}>Enter keypair</CompactButton>
                    {keypairStatus.configured && <CompactButton onClick={() => setKeypairAction("revoke")}>Revoke</CompactButton>}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Enabled toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-ink-gray-700 dark:text-ink-gray-300">Enable Gluon Link</p>
              <p className="text-xs text-ink-gray-500 dark:text-ink-gray-400">Route agent requests through Gluon Link</p>
            </div>
            <Toggle
              checked={form.enabled}
              onChange={(v) => setForm((prev) => ({ ...prev, enabled: v }))}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <PrimaryButton onClick={handleSave} disabled={saving || !pageDirty}>
              {saving ? "Saving…" : "Save"}
            </PrimaryButton>
            <CompactButton onClick={discardDrafts} disabled={saving || !pageDirty}>Cancel</CompactButton>
          </div>
        </div>
      )}
      <ConfirmDialog
        open={confirmingSave}
        title="Save Gluon Link changes?"
        confirmLabel="Save changes"
        loadingLabel="Saving…"
        confirmVariant={keypairAction === "revoke" ? "danger" : "primary"}
        isLoading={saving}
        onCancel={() => setConfirmingSave(false)}
        onConfirm={persistChanges}
      >
        {keypairAction === "revoke"
          ? "The private and public keys will be deleted together. Local JWT signing will stop immediately."
          : "The private and public keys will be replaced together. Tokens signed by the previous key may stop validating."}
      </ConfirmDialog>
    </SettingsPageShell>
  );
};

export default ConfigGluonPage;
